import type { BackendGameComponentData } from '@core/types/backend-game.types';
import type { CascadeSlotConfig, CascadeSymbolConfig, PaytableBracket, MultiplierPoolEntry } from '@core/types/cascade-slot.types';
import { DEFAULT_CASCADE_SLOT_CONFIG } from '@core/types/cascade-slot.types';
import { augmentSymbolsWithGolden, remapNormalSymbolCodes } from './GoldenSymbolGenerator';

const CATEGORY_COLORS: Record<string, { color: string; textColor: string }> = {
  high: { color: '#6B7280', textColor: '#FFFFFF' },
  normal: { color: '#9CA3AF', textColor: '#000000' },
  scatter: { color: '#F59E0B', textColor: '#000000' },
  bonus: { color: '#EC4899', textColor: '#FFFFFF' },
  wild: { color: '#F97316', textColor: '#FFFFFF' },
  golden: { color: '#CA8A04', textColor: '#FFFFFF' },
  system: { color: '#6B7280', textColor: '#FFFFFF' },
};
const HIGH_COLORS = ['#6B7280', '#78716C', '#71717A', '#737373'];
const NORMAL_COLORS = ['#9CA3AF', '#A8A29E', '#A1A1AA', '#A3A3A3', '#94A3B8'];
const GOLDEN_HIGH_COLORS = ['#92702F', '#7C6A2F', '#8B7332', '#6B5B2F'];
const GOLDEN_NORMAL_COLORS = ['#A8923F', '#9B8838', '#B09940', '#8C7A30', '#A08535'];

/** Map backend category to local CascadeSymbolCategory */
function mapCategory(category: string): CascadeSymbolConfig['category'] {
  switch (category) {
    case 'high': return 'high';
    case 'normal': return 'normal';
    case 'scatter': return 'scatter';
    case 'bonus': return 'bonus';
    case 'wild': return 'wild';
    case 'golden': return 'high'; // golden symbols treated as high-pay
    default: return 'normal';
  }
}

export function convertBackendToSlotConfig(
  data: BackendGameComponentData
): CascadeSlotConfig {
  const cfg = data.backendConfig;
  if (!cfg) return { ...DEFAULT_CASCADE_SLOT_CONFIG };

  const modeIndex = data.activeMode ?? 0;
  const mode = cfg.modes?.[modeIndex] ?? cfg.modes?.[0];

  // Build paytable tiers from whichever format the API provides
  const symbolCountTiers = cfg.payTable?.symbolCountTiers;
  const numOfKindTiers = cfg.payTable?.numOfKindTiers;

  const symbols: CascadeSymbolConfig[] = [];
  let highIdx = 0, normalIdx = 0, goldenHighIdx = 0, goldenNormalIdx = 0;

  const augmented = augmentSymbolsWithGolden(cfg.symbols ?? [], cfg.mechanics);
  const allSymbols = remapNormalSymbolCodes(augmented);

  // Build reverse mapping: remapped code → original code (for looking up raw API data)
  const originalCodeMap = new Map<string, string>();
  for (let i = 0; i < augmented.length; i++) {
    originalCodeMap.set(allSymbols[i].code, augmented[i].code);
  }

  for (const sym of allSymbols) {
    if (sym.category === 'system') continue;

    const originalCode = originalCodeMap.get(sym.code) ?? sym.code;
    const mappedCategory = mapCategory(sym.category);
    let color = CATEGORY_COLORS[sym.category]?.color ?? '#6B7280';
    const textColor = CATEGORY_COLORS[sym.category]?.textColor ?? '#FFFFFF';

    if (sym.category === 'high') color = HIGH_COLORS[highIdx++ % HIGH_COLORS.length];
    if (sym.category === 'normal') color = NORMAL_COLORS[normalIdx++ % NORMAL_COLORS.length];
    if (sym.category === 'golden') {
      // Golden symbols get darker shade of their base type
      if (sym.code.startsWith('H')) {
        color = GOLDEN_HIGH_COLORS[goldenHighIdx++ % GOLDEN_HIGH_COLORS.length];
      } else {
        color = GOLDEN_NORMAL_COLORS[goldenNormalIdx++ % GOLDEN_NORMAL_COLORS.length];
      }
    }

    const symbolWeight = mode?.symbolWeight;
    let baseWeight: number;
    let freeWeight: number;
    if (symbolWeight) {
      // Look up using original code since symbolOrder comes from raw API
      const symOrderIdx = symbolWeight.symbolOrder?.indexOf(originalCode) ?? -1;
      baseWeight = symOrderIdx >= 0 ? (symbolWeight.BaseGame?.[symOrderIdx] ?? 0) : 0;
      freeWeight = symOrderIdx >= 0 ? (symbolWeight.FreeGame?.[symOrderIdx] ?? 0) : 0;
    } else {
      const defaultWeights: Record<string, number> = {
        high: 80, normal: 100, scatter: 5, bonus: 10, wild: 8, golden: 0,
      };
      baseWeight = defaultWeights[sym.category] ?? 50;
      freeWeight = defaultWeights[sym.category] ?? 50;
    }

    // Look up paytable using original code since payTable comes from raw API
    const regularEntry = cfg.payTable?.regular?.find(r => r.code === originalCode);
    const paytable: PaytableBracket[] = [];
    if (regularEntry) {
      if (symbolCountTiers && symbolCountTiers.length > 0) {
        // Gates of Olympus format: { min, max }
        for (let i = 0; i < symbolCountTiers.length; i++) {
          const tier = symbolCountTiers[i];
          paytable.push({
            minCount: tier.min,
            maxCount: tier.max,
            payout: regularEntry.payouts?.[i] ?? 0,
          });
        }
      } else if (numOfKindTiers && numOfKindTiers.length > 0) {
        // Mahjong Ways 2 format: { count }
        for (let i = 0; i < numOfKindTiers.length; i++) {
          const tier = numOfKindTiers[i];
          const nextCount = numOfKindTiers[i + 1]?.count;
          paytable.push({
            minCount: tier.count,
            maxCount: nextCount ? nextCount - 1 : tier.count,
            payout: regularEntry.payouts?.[i] ?? 0,
          });
        }
      }
    }

    // Check both remapped and original code for texture (handles legacy N-prefix saves)
    const texturePath = data.symbolTextures?.[sym.code] ?? data.symbolTextures?.[originalCode];

    symbols.push({
      id: sym.code,
      name: sym.name,
      category: mappedCategory,
      color,
      textColor,
      weight: baseWeight,
      freeGameWeight: freeWeight,
      paytable,
      displayMode: texturePath ? 'sprite' : 'color',
      textureAssetPath: texturePath,
    });
  }

  const multiplierWeights = mode?.multiplierWeight;
  // options.multiplier may be number[] (Gates of Olympus) or { BaseGame: number[], FreeGame: number[] } (Speed & Fury)
  const rawMultiplier = cfg.options?.multiplier;
  const multiplierValues: number[] = Array.isArray(rawMultiplier)
    ? rawMultiplier
    : (rawMultiplier && 'BaseGame' in rawMultiplier) ? rawMultiplier.BaseGame : [];
  const multiplierPool: MultiplierPoolEntry[] = multiplierValues.map((value, i) => ({
    value,
    weight: multiplierWeights?.[i] ?? 10,
  }));

  // Derive countBrackets from whichever tier format is available
  let countBrackets: number[];
  if (cfg.options?.symbolCount && cfg.options.symbolCount.length > 0) {
    countBrackets = cfg.options.symbolCount;
  } else if (numOfKindTiers && numOfKindTiers.length > 0) {
    countBrackets = numOfKindTiers.map(t => t.count);
  } else if (symbolCountTiers && symbolCountTiers.length > 0) {
    countBrackets = symbolCountTiers.map(t => t.min);
  } else {
    countBrackets = [8, 10, 15, 20, 25, 30];
  }

  const totalBet = cfg.options?.totalBet ?? [20];
  const rules = cfg.rules;
  const mechanics = cfg.mechanics;

  // Find the scatter symbol code for free spins trigger
  const scatterSym = cfg.symbols?.find(s => s.category === 'scatter');
  const scatterCode = scatterSym?.code ?? 'SC';

  // Derive grid dimensions: support both fixed (rows/cols) and variable (reels/reelHeights) formats
  const reelHeights = cfg.grid?.reelHeights;
  const hasVariableReels = reelHeights && reelHeights.length > 0;
  const gridRows = hasVariableReels
    ? Math.max(...reelHeights)
    : (cfg.grid?.rows ?? 5);
  const gridColumns = cfg.grid?.reels ?? cfg.grid?.cols ?? 6;

  // Determine win evaluation type from mechanics
  const winType: 'count' | 'ways' = mechanics?.winType === 'ways-pay' ? 'ways' : 'count';

  return {
    rows: gridRows,
    columns: gridColumns,
    ...(hasVariableReels ? { reelHeights } : {}),
    winType,
    symbolWidth: 120,
    symbolHeight: 100,
    symbolSpacing: 4,
    symbols,
    minMatchCount: rules?.minNumOfKind ?? countBrackets[0] ?? 8,
    countBrackets,
    cascadeEnabled: mechanics?.tumble ?? true,
    cascadeDirection: 'horizontal',
    cascadeAnimSpeed: 300,
    multiplierPool,
    freeSpins: {
      triggerSymbolId: scatterCode,
      triggerCount: rules?.triggerCount ?? 4,
      spinsAwarded: rules?.freeSpins ?? 15,
      retriggerCount: rules?.retriggerCount ?? 3,
      retriggerSpinsAwarded: rules?.addFreeSpins ?? 5,
    },
    betLevels: data.customBetLevels ?? totalBet,
    startingBalance: data.startingBalance ?? 10000,
    buyFeatureEnabled: mechanics?.buyFeature ?? false,
    buyFeatureCostMultiplier: 100,
    maxWinMultiplier: (rules?.maxWin ?? 5000) / (totalBet[0] ?? 20),
    spinDuration: 0.5,
    turboMultiplier: 3,
    winPresentDuration: 0.8,
    cascadeRemoveDuration: 0.3,
    cascadeFillDuration: 0.5,
    cascadeGravityDuration: 0.25,
    cascadeColumnDelay: 30,
    winHighlightScale: 1.1,
    cascadeFallStyle: 'bounce',
    // Extended fields for backend games
    scatterPaytable: (cfg.payTable?.scatter?.countTiers ?? []).map(t => ({
      count: t.count,
      payout: t.payout ?? 0,
    })),
    superScatterPaytable: (cfg.payTable?.superScatter?.countTiers ?? []).map(t => ({
      count: t.count,
      payout: t.payout ?? 0,
    })),
    superScatterRate: cfg.superScatterRate ?? [],
    maxWin: rules?.maxWin ?? 5000,
    buyFeatureScatterCount: cfg.buyFeatureScatterCount ?? [],
    hasSuperScatter: mechanics?.superScatter ?? false,
  };
}
